import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router';
import { cvDownloadAdres } from '../../api/cvs.ts';
import { useVoordracht } from '../../api/voordrachten.ts';
import { Kenmerken } from '../../componenten/Kenmerken.tsx';
import { Laadstatus } from '../../componenten/Laadstatus.tsx';
import { Pagina } from '../../componenten/Pagina.tsx';
import { StatusChip } from '../../componenten/StatusChip.tsx';
import { bedrag, datum, datumTijd, volledigeNaam } from '../../helpers/formatteer.ts';
import { voordrachtfaseLabels } from '../../helpers/labels.ts';
import { AfrondDialoog } from './AfrondDialoog.tsx';
import { FaseDialoog } from './FaseDialoog.tsx';

/**
 * De pagina van een voordracht: kandidaat, vacature, cv, fase, uitkomst en de geschiedenis van stappen.
 * @returns De pagina.
 */
export function VoordrachtPagina() {
  const { id = '' } = useParams();
  const voordracht = useVoordracht(id);
  const [fase, setFase] = useState<'fase' | 'heropen' | null>(null);
  const [afronden, setAfronden] = useState(false);

  return (
    <Laadstatus query={voordracht}>
      {(details) => {
        const lopend = details.uitkomst === 'lopend';
        return (
          <Pagina
            titel={`${volledigeNaam(details.kandidaat)} voor ${details.vacature.titel}`}
            terug={{ naar: '/voordrachten', label: 'Voordrachten' }}
            ondertitel={
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 1 }}>
                <StatusChip soort="fase" waarde={details.fase} />
                <StatusChip soort="uitkomst" waarde={details.uitkomst} />
              </Stack>
            }
            acties={
              lopend ? (
                <>
                  <Button variant="outlined" startIcon={<SwapHorizIcon />} onClick={() => setFase('fase')}>
                    Naar andere fase
                  </Button>
                  <Button variant="contained" startIcon={<CheckCircleOutlinedIcon />} onClick={() => setAfronden(true)}>
                    Afronden
                  </Button>
                </>
              ) : (
                <Button variant="outlined" startIcon={<ReplayIcon />} onClick={() => setFase('heropen')}>
                  Heropenen
                </Button>
              )
            }
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 5 }}>
                <Card>
                  <CardHeader title="Gegevens" slotProps={{ title: { variant: 'h6' } }} sx={{ pb: 0 }} />
                  <CardContent>
                    <Kenmerken
                      kolommen={1}
                      items={[
                        {
                          label: 'Kandidaat',
                          waarde: (
                            <Link component={RouterLink} to={`/kandidaten/${details.kandidaatId}`} underline="hover">
                              {volledigeNaam(details.kandidaat)}
                            </Link>
                          ),
                        },
                        {
                          label: 'Vacature',
                          waarde: (
                            <Link component={RouterLink} to={`/vacatures/${details.vacatureId}`} underline="hover">
                              {details.vacature.titel}
                            </Link>
                          ),
                        },
                        {
                          label: 'Meegestuurd cv',
                          waarde: details.cv ? (
                            <Link href={cvDownloadAdres(details.cv.id)} underline="hover">
                              {details.cv.bestandsnaam}
                            </Link>
                          ) : null,
                        },
                        { label: 'Startdatum plaatsing', waarde: datum(details.startdatumPlaatsing) },
                        { label: 'Tarief', waarde: bedrag(details.tarief) },
                        { label: 'Voorgedragen op', waarde: datumTijd(details.aangemaaktOp) },
                        { label: 'Laatst gewijzigd', waarde: datumTijd(details.gewijzigdOp) },
                      ]}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, md: 7 }}>
                <Card>
                  <CardHeader title="Geschiedenis" slotProps={{ title: { variant: 'h6' } }} sx={{ pb: 0 }} />
                  <CardContent>
                    {details.stappen.length === 0 ? (
                      <Typography color="text.secondary">Nog geen stappen.</Typography>
                    ) : (
                      <List disablePadding>
                        {[...details.stappen].reverse().map((stap) => (
                          <ListItem key={stap.id} disableGutters divider alignItems="flex-start">
                            <ListItemText
                              primary={voordrachtfaseLabels[stap.naarFase]}
                              secondary={
                                <>
                                  {datumTijd(stap.op)}
                                  {stap.gebruiker ? ` door ${stap.gebruiker.naam}` : ''}
                                  {stap.notitie && (
                                    <Typography
                                      component="span"
                                      variant="body2"
                                      sx={{ display: 'block', whiteSpace: 'pre-wrap', mt: 0.5 }}
                                    >
                                      {stap.notitie}
                                    </Typography>
                                  )}
                                </>
                              }
                              slotProps={{ secondary: { component: 'div' } }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            {fase && <FaseDialoog open onSluit={() => setFase(null)} voordracht={details} modus={fase} />}
            <AfrondDialoog open={afronden} onSluit={() => setAfronden(false)} voordracht={details} />
          </Pagina>
        );
      }}
    </Laadstatus>
  );
}
